import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import { getLang } from 'shared/store/selectors/lang.selector';
import type { KnowledgeAttachment } from 'shared/types/knowledge';
import { Button, Loader } from 'src/components/core';
import { buildIframeAllowRegex } from 'src/components/knowledge-content/components/knowledge-viewers/iframe-allowlist';
import styles from './attachment-viewer.module.scss';

// toolbar=0 hides the native PDF toolbar (download/print) for embedded previews
const PDF_VIEWER_PARAMS = '#toolbar=0';

type AttachmentViewerProps = {
  attachment: KnowledgeAttachment;
};

function OpenExternally({ attachment }: { attachment: KnowledgeAttachment }) {
  const lang = useSelector(getLang('EXTERNAL_LINK_VIEWER'));

  return (
    <div className={styles.fallback}>
      <span className={styles.fallbackHeader}>{lang.LINK_NOT_AVAILABLE_HEADER}</span>
      <span>{lang.LINK_NOT_AVAILABLE_SUBHEADER}</span>
      <Button
        size="small"
        buttonStyle="primary"
        onClick={() => window.open(attachment.link, '_blank', 'noopener')}>
        {attachment.title}
      </Button>
    </div>
  );
}

export function AttachmentViewer({ attachment }: AttachmentViewerProps) {
  const [mediaFailed, setMediaFailed] = useState(false);
  const [frameLoading, setFrameLoading] = useState(true);
  const allowIframeDomains =
    useSelector(
      (state: { generalConfig: { ALLOW_IFRAME_DOMAINS?: string[] } }) =>
        state.generalConfig.ALLOW_IFRAME_DOMAINS,
    ) || [];
  const { title, link, type } = attachment;

  if (mediaFailed) {
    return <OpenExternally attachment={attachment} />;
  }

  switch (type) {
    case 'image':
      return (
        <img
          className={styles.image}
          src={link}
          alt={title}
          onError={() => setMediaFailed(true)}
        />
      );
    case 'video':
      return (
        <video
          className={styles.video}
          src={link}
          title={title}
          controls
          onError={() => setMediaFailed(true)}
        />
      );
    case 'pdf':
      // Same embed policy as ExternalLinkViewer: never iframe a non-allowlisted host
      if (!allowIframeDomains.length || !buildIframeAllowRegex(allowIframeDomains).test(link)) {
        return <OpenExternally attachment={attachment} />;
      }
      return (
        <>
          <iframe
            className={frameLoading ? styles.hiddenFrame : styles.frame}
            src={`${link}${PDF_VIEWER_PARAMS}`}
            title={title}
            onLoad={() => setFrameLoading(false)}
          />
          {frameLoading && (
            <div className={styles.loaderWrapper}>
              <Loader />
            </div>
          )}
        </>
      );
    default:
      return <OpenExternally attachment={attachment} />;
  }
}
